/*
 * Painel gerencial: como a rede está sendo usada, por praça.
 *
 * Escrito para quem acompanha o serviço de fora — fiscalização, planejamento
 * de ampliação. Por isso só números agregados (nenhum nome ou telefone) e
 * sempre a comparação ao lado: "1.240 acessos" não diz se a praça está
 * crescendo; "+18% sobre os 30 dias anteriores" diz.
 *
 * Localidade é a VLAN: cada praça tem a sua.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiRequestError, apiList, apiRequest } from '../lib/api';
import {
  formatarBytes,
  nomeDaLocalidade,
  type MikrotikDto,
  type OnlineNowDto,
  type UsageReportDto,
} from '../lib/types';
import { GraficoDeSerie, GraficoPorDia, MapaDeHorarios } from '../components/Charts';
import { Alert, Button, Card, Spinner, StatCard } from '../components/ui';

/** O "agora" se atualiza sozinho; o histórico só quando o filtro muda. */
const INTERVALO_AO_VIVO_MS = 30_000;

const PRESETS = [
  { chave: 'hoje', rotulo: 'Hoje', dias: 1 },
  { chave: '7', rotulo: '7 dias', dias: 7 },
  { chave: '30', rotulo: '30 dias', dias: 30 },
  { chave: '90', rotulo: '90 dias', dias: 90 },
] as const;

type Metrica = 'acessos' | 'dispositivos' | 'trafego' | 'pico';

const METRICAS: { chave: Metrica; rotulo: string }[] = [
  { chave: 'acessos', rotulo: 'Acessos' },
  { chave: 'dispositivos', rotulo: 'Aparelhos' },
  { chave: 'trafego', rotulo: 'Tráfego' },
  { chave: 'pico', rotulo: 'Pico de conectados' },
];

/** Hoje em Brasília, como YYYY-MM-DD. */
function hoje(): string {
  const [d, m, a] = new Date()
    .toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })
    .split('/') as [string, string, string];
  return `${a}-${m}-${d}`;
}

function somarDias(iso: string, dias: number): string {
  const data = new Date(`${iso}T00:00:00Z`);
  data.setUTCDate(data.getUTCDate() + dias);
  return data.toISOString().slice(0, 10);
}

function dataBr(iso: string): string {
  return iso.split('-').reverse().join('/');
}

function inteiro(valor: number): string {
  return valor.toLocaleString('pt-BR');
}

/** "+18% vs. período anterior", ou nada quando não há base para comparar. */
function variacao(atual: number, anterior: number): string | undefined {
  if (anterior === 0) return atual > 0 ? 'sem dados no período anterior' : undefined;
  const pct = Math.round(((atual - anterior) / anterior) * 100);
  return `${pct > 0 ? '+' : ''}${pct}% vs. período anterior`;
}

function dataHoraCurta(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function UsagePanel() {
  const [preset, setPreset] = useState<string>('30');
  const [de, setDe] = useState(() => somarDias(hoje(), -29));
  const [ate, setAte] = useState(() => hoje());
  const [mikrotikId, setMikrotikId] = useState<number | null>(null);
  const [vlan, setVlan] = useState<string | null>(null);
  const [metrica, setMetrica] = useState<Metrica>('acessos');

  const [equipamentos, setEquipamentos] = useState<MikrotikDto[]>([]);
  const [relatorio, setRelatorio] = useState<UsageReportDto | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    apiList<MikrotikDto>('/admin/mikrotiks?perPage=100')
      .then(({ data }) => setEquipamentos(data))
      .catch(() => setEquipamentos([]));
  }, []);

  const consulta = useMemo(() => {
    const partes = [`de=${de}`, `ate=${ate}`];
    if (mikrotikId !== null) partes.push(`mikrotikId=${mikrotikId}`);
    if (vlan) partes.push(`vlan=${encodeURIComponent(vlan)}`);
    return `?${partes.join('&')}`;
  }, [de, ate, mikrotikId, vlan]);

  useEffect(() => {
    let ativo = true;
    setRelatorio(null);
    setErro(null);
    apiRequest<UsageReportDto>(`/admin/reports/usage${consulta}`)
      .then((dados) => ativo && setRelatorio(dados))
      .catch(
        (caught: unknown) =>
          ativo &&
          setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao carregar'),
      );
    return () => {
      ativo = false;
    };
  }, [consulta]);

  function escolherPreset(chave: string, dias: number) {
    setPreset(chave);
    const fim = hoje();
    setAte(fim);
    setDe(somarDias(fim, -(dias - 1)));
  }

  const localidadesDoFiltro = (relatorio?.localidades ?? []).filter(
    (local) => mikrotikId === null || local.mikrotikId === mikrotikId,
  );
  const variosEquipamentos = equipamentos.length > 1;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Uso da rede</h1>
          <p className="text-sm text-slate-500">
            {dataBr(de)} a {dataBr(ate)}
            {vlan ? ` · ${nomeDaLocalidade(vlan)}` : ''}, no fuso de Brasília.
          </p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <a
            href={`/api/admin/reports/usage.csv${consulta}`}
            className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            Exportar CSV
          </a>
          <Button variant="ghost" onClick={() => window.print()}>
            Imprimir
          </Button>
        </div>
      </header>

      <Card className="print:hidden">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 text-sm shadow-sm">
            {PRESETS.map((opcao) => (
              <button
                key={opcao.chave}
                type="button"
                onClick={() => escolherPreset(opcao.chave, opcao.dias)}
                className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
                  preset === opcao.chave
                    ? 'bg-brand-600 text-white'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {opcao.rotulo}
              </button>
            ))}
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-600">
            De
            <input
              type="date"
              value={de}
              max={ate}
              onChange={(evento) => {
                if (!evento.target.value) return;
                setPreset('');
                setDe(evento.target.value);
              }}
              className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm shadow-sm"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            até
            <input
              type="date"
              value={ate}
              min={de}
              max={hoje()}
              onChange={(evento) => {
                if (!evento.target.value) return;
                setPreset('');
                setAte(evento.target.value);
              }}
              className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm shadow-sm"
            />
          </label>

          {variosEquipamentos ? (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Equipamento
              <select
                value={mikrotikId ?? ''}
                onChange={(evento) => {
                  setMikrotikId(evento.target.value ? Number(evento.target.value) : null);
                  setVlan(null);
                }}
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm"
              >
                <option value="">Todos</option>
                {equipamentos.map((equipamento) => (
                  <option key={equipamento.id} value={equipamento.id}>
                    {equipamento.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {localidadesDoFiltro.length > 0 ? (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Localidade
              <select
                value={vlan ?? ''}
                onChange={(evento) => setVlan(evento.target.value || null)}
                className="max-w-64 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm"
              >
                <option value="">Todas</option>
                {localidadesDoFiltro.map((local) => (
                  <option key={`${local.mikrotikId}|${local.vlan}`} value={local.vlan}>
                    {nomeDaLocalidade(local.vlan)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      </Card>

      <ConectadosAgora
        mikrotikId={mikrotikId}
        vlan={vlan}
        variosEquipamentos={variosEquipamentos}
      />

      {erro ? <Alert>{erro}</Alert> : null}

      {!relatorio ? (
        erro ? null : (
          <Spinner label="Montando o relatório..." />
        )
      ) : (
        <>
          <AvisoDeColeta coletaDesde={relatorio.resumo.coletaDesde} de={relatorio.periodo.de} />

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              label="Acessos"
              value={inteiro(relatorio.resumo.acessos)}
              hint={variacao(relatorio.resumo.acessos, relatorio.resumo.anterior.acessos)}
            />
            <StatCard
              label="Aparelhos distintos"
              value={inteiro(relatorio.resumo.dispositivos)}
              hint={variacao(relatorio.resumo.dispositivos, relatorio.resumo.anterior.dispositivos)}
            />
            <StatCard
              label="Tráfego entregue"
              value={formatarBytes(relatorio.resumo.bytesDown)}
              hint={`${formatarBytes(relatorio.resumo.bytesUp)} enviados pelos usuários`}
            />
            <StatCard
              label="Pico de conectados"
              value={inteiro(relatorio.resumo.picoSimultaneos)}
              hint={
                relatorio.resumo.picoEm
                  ? `${dataHoraCurta(relatorio.resumo.picoEm)} · média ${relatorio.resumo.mediaSimultaneos.toLocaleString('pt-BR')}`
                  : 'sem amostras no período'
              }
            />
          </div>

          <Card
            title="Evolução por dia"
            action={
              <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 text-xs shadow-sm print:hidden">
                {METRICAS.map((opcao) => (
                  <button
                    key={opcao.chave}
                    type="button"
                    onClick={() => setMetrica(opcao.chave)}
                    className={`rounded-md px-2.5 py-1 font-medium transition-colors ${
                      metrica === opcao.chave
                        ? 'bg-brand-600 text-white'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {opcao.rotulo}
                  </button>
                ))}
              </div>
            }
          >
            {metrica === 'acessos' ? (
              <GraficoPorDia
                pontos={relatorio.porDia.map((ponto) => ({
                  dia: ponto.dia,
                  total: ponto.acessos,
                  liberados: ponto.liberados,
                }))}
              />
            ) : metrica === 'dispositivos' ? (
              <GraficoDeSerie
                rotulo="Aparelhos distintos por dia"
                pontos={relatorio.porDia.map((ponto) => ({
                  dia: ponto.dia,
                  valor: ponto.dispositivos,
                }))}
                formatar={inteiro}
              />
            ) : metrica === 'trafego' ? (
              <GraficoDeSerie
                rotulo="Tráfego entregue por dia"
                pontos={relatorio.porDia.map((ponto) => ({
                  dia: ponto.dia,
                  valor: ponto.bytesDown,
                }))}
                formatar={formatarBytes}
              />
            ) : (
              <GraficoDeSerie
                rotulo="Pico de conectados ao mesmo tempo por dia"
                pontos={relatorio.porDia.map((ponto) => ({
                  dia: ponto.dia,
                  valor: ponto.picoSimultaneos,
                }))}
                formatar={inteiro}
              />
            )}
          </Card>

          <Card title="Uso por localidade">
            <TabelaDeLocalidades
              linhas={relatorio.porLocalidade}
              mostrarEquipamento={variosEquipamentos && mikrotikId === null}
              onEscolher={(local) => {
                if (!local.vlan) return;
                setMikrotikId(local.mikrotikId);
                setVlan(local.vlan);
              }}
            />
          </Card>

          <Card title="Horários de maior utilização">
            <MapaDeHorarios celulas={relatorio.porHorario} />
          </Card>
        </>
      )}
    </div>
  );
}

/** Antes da coleta existir não há tráfego registrado — e não "tráfego zero". */
function AvisoDeColeta({ coletaDesde, de }: { coletaDesde: string | null; de: string }) {
  if (!coletaDesde) {
    return (
      <Alert tone="info">
        A coleta de tráfego e de conectados simultâneos ainda não registrou nenhuma amostra. Os
        números de acessos e aparelhos já valem para todo o histórico; tráfego e pico passam a
        aparecer alguns minutos depois que o worker estiver rodando.
      </Alert>
    );
  }
  if (coletaDesde.slice(0, 10) > de) {
    return (
      <Alert tone="info">
        Tráfego e conectados simultâneos são registrados desde{' '}
        <strong>{dataHoraCurta(coletaDesde)}</strong>. Antes disso, o período mostra só acessos e
        aparelhos.
      </Alert>
    );
  }
  return null;
}

export function ConectadosAgora({
  mikrotikId,
  vlan,
  variosEquipamentos,
}: {
  mikrotikId: number | null;
  vlan: string | null;
  variosEquipamentos: boolean;
}) {
  const [dados, setDados] = useState<OnlineNowDto[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null);

  const carregar = useCallback(async () => {
    try {
      const resposta = await apiRequest<OnlineNowDto[]>(
        `/admin/reports/online-now${mikrotikId !== null ? `?mikrotikId=${mikrotikId}` : ''}`,
      );
      setDados(resposta);
      setErro(null);
      setAtualizadoEm(new Date());
    } catch (caught) {
      // Mantém a última leitura: sumir com os números seria dizer "ninguém".
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao consultar');
    }
  }, [mikrotikId]);

  useEffect(() => {
    setDados(null);
    void carregar();
    const timer = window.setInterval(() => void carregar(), INTERVALO_AO_VIVO_MS);
    return () => window.clearInterval(timer);
  }, [carregar]);

  const linhas = (dados ?? []).flatMap((equipamento) =>
    equipamento.localidades
      .filter((local) => !vlan || local.vlan === vlan)
      .map((local) => ({
        ...local,
        equipamento: equipamento.mikrotikName,
        id: equipamento.mikrotikId,
      })),
  );
  const total = linhas.reduce((soma, linha) => soma + linha.online, 0);
  const maximo = Math.max(1, ...linhas.map((linha) => linha.online));
  const semResposta = (dados ?? []).filter((equipamento) => !equipamento.ok);

  return (
    <Card
      title="Conectados agora"
      action={
        atualizadoEm ? (
          <span className="text-xs text-slate-500">
            ao vivo · atualizado {atualizadoEm.toLocaleTimeString('pt-BR')}
          </span>
        ) : null
      }
    >
      {!dados ? (
        erro ? (
          <Alert>{erro}</Alert>
        ) : (
          <Spinner label="Consultando os equipamentos..." />
        )
      ) : (
        <div className="space-y-4">
          {erro ? <Alert tone="warning">{erro} Mostrando a última leitura.</Alert> : null}
          {semResposta.length > 0 ? (
            <Alert tone="warning">
              Sem resposta de{' '}
              {semResposta.map((equipamento) => equipamento.mikrotikName).join(', ')}: os conectados
              desse equipamento não entram no total.
            </Alert>
          ) : null}

          <p className="text-4xl font-bold text-slate-900 tabular-nums">
            {inteiro(total)}
            <span className="ml-2 text-sm font-medium text-slate-500">
              {total === 1 ? 'pessoa conectada' : 'pessoas conectadas'}
            </span>
          </p>

          {linhas.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhuma localidade com hotspot ativo.</p>
          ) : (
            <ul className="grid gap-x-8 gap-y-2.5 md:grid-cols-2">
              {linhas.map((linha) => (
                <li key={`${linha.id}|${linha.vlan ?? ''}`}>
                  <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
                    <span className="truncate text-slate-700" title={linha.vlan ?? undefined}>
                      {nomeDaLocalidade(linha.vlan)}
                      {variosEquipamentos ? (
                        <span className="ml-1.5 text-xs text-slate-400">{linha.equipamento}</span>
                      ) : null}
                    </span>
                    <span className="shrink-0 font-semibold tabular-nums text-slate-800">
                      {linha.online}
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-emerald-500"
                      style={{ width: `${(linha.online / maximo) * 100}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

function TabelaDeLocalidades({
  linhas,
  mostrarEquipamento,
  onEscolher,
}: {
  linhas: UsageReportDto['porLocalidade'];
  mostrarEquipamento: boolean;
  onEscolher: (local: UsageReportDto['porLocalidade'][number]) => void;
}) {
  if (linhas.length === 0) {
    return <p className="text-sm text-slate-500">Nenhum uso registrado no período.</p>;
  }

  const totalAcessos = linhas.reduce((soma, linha) => soma + linha.acessos, 0);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-xs tracking-wide text-slate-500 uppercase">
            <th className="py-2 pr-3">Localidade</th>
            {mostrarEquipamento ? <th className="py-2 pr-3">Equipamento</th> : null}
            <th className="py-2 pr-3 text-right">Acessos</th>
            <th className="py-2 pr-3 text-right">Aparelhos</th>
            <th className="py-2 pr-3 text-right">Download</th>
            <th className="py-2 pr-3 text-right">Upload</th>
            <th className="py-2 pr-3 text-right">Pico</th>
            <th className="py-2 text-right">Média</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((linha) => (
            <tr
              key={`${linha.mikrotikId}|${linha.vlan ?? ''}`}
              onClick={() => onEscolher(linha)}
              className={`border-b border-slate-100 last:border-0 ${
                linha.vlan ? 'cursor-pointer hover:bg-slate-50' : ''
              }`}
              title={linha.vlan ? 'Ver só esta localidade' : undefined}
            >
              <td className="py-2.5 pr-3">
                <span className="font-medium text-slate-800">{nomeDaLocalidade(linha.vlan)}</span>
                {linha.vlan ? (
                  <span className="block font-mono text-[11px] text-slate-400">{linha.vlan}</span>
                ) : null}
              </td>
              {mostrarEquipamento ? (
                <td className="py-2.5 pr-3 text-slate-600">{linha.mikrotikName}</td>
              ) : null}
              <td className="py-2.5 pr-3 text-right tabular-nums text-slate-700">
                {inteiro(linha.acessos)}
                {totalAcessos ? (
                  <span className="ml-1.5 text-xs text-slate-400">
                    {Math.round((linha.acessos / totalAcessos) * 100)}%
                  </span>
                ) : null}
              </td>
              <td className="py-2.5 pr-3 text-right tabular-nums text-slate-700">
                {inteiro(linha.dispositivos)}
              </td>
              <td className="py-2.5 pr-3 text-right tabular-nums text-slate-700">
                {formatarBytes(linha.bytesDown)}
              </td>
              <td className="py-2.5 pr-3 text-right tabular-nums text-slate-500">
                {formatarBytes(linha.bytesUp)}
              </td>
              <td className="py-2.5 pr-3 text-right tabular-nums text-slate-700">
                {inteiro(linha.picoSimultaneos)}
              </td>
              <td className="py-2.5 text-right tabular-nums text-slate-500">
                {linha.mediaSimultaneos.toLocaleString('pt-BR')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-3 text-xs text-slate-500">
        <strong>Aparelhos</strong> conta cada celular uma vez no período, mesmo que tenha acessado
        vários dias. <strong>Pico</strong> e <strong>média</strong> são de pessoas conectadas ao
        mesmo tempo, medidas a cada 5 minutos. Clique numa linha para ver só aquela localidade.
      </p>
    </div>
  );
}
